import { useState, useEffect, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/types';
import { getUserProfile } from '@/lib/supabase-auth';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const isProfileFetchingRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  const fetchUserProfile = useCallback(async (userId: string, forceRefetch = false) => {
    // Prevent duplicate fetches for the same user
    if (isProfileFetchingRef.current) {
      console.log("useAuth: Already fetching profile, skipping");
      return;
    }
    
    // Skip if we already fetched this user successfully (unless forced)
    if (!forceRefetch && lastUserIdRef.current === userId) {
      console.log("useAuth: Profile already fetched for this user");
      setLoading(false);
      return;
    }
    
    isProfileFetchingRef.current = true;
    lastUserIdRef.current = userId;
    
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn("useAuth: Profile fetch timeout, setting loading to false");
      setLoading(false);
      isProfileFetchingRef.current = false;
    }, 5000);
    
    try {
      console.log("useAuth: Fetching user profile for:", userId);
      const { data } = await getUserProfile(userId);
      console.log("useAuth: Profile data:", data);
      setUserRole(data?.role as UserRole || null);
    } catch (error) {
      console.error("useAuth: Error fetching profile:", error);
      setUserRole(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      isProfileFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    
    console.log("useAuth: Initializing auth hook");
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        console.log("useAuth: Auth state change:", { 
          event, 
          hasSession: !!session, 
          hasUser: !!session?.user,
          userId: session?.user?.id 
        });
        
        // Reset all state first when signing out
        if (event === 'SIGNED_OUT' || !session) {
          console.log("useAuth: Clearing auth state");
          setSession(null);
          setUser(null);
          setUserRole(null);
          setLoading(false);
          lastUserIdRef.current = null;
          isProfileFetchingRef.current = false;
          return;
        }
        
        // Handle sign in
        console.log("useAuth: Setting session and user");
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log("useAuth: Fetching profile for user:", session.user.id);
          fetchUserProfile(session.user.id);
        } else {
          console.log("useAuth: No user in session, stopping loading");
          setLoading(false);
        }
      }
    );

    // Check for existing session
    console.log("useAuth: Checking for existing session");
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;
      
      if (error) {
        console.error("useAuth: Error getting session:", error);
        setLoading(false);
        return;
      }
      
      console.log("useAuth: Initial session check:", { 
        hasSession: !!session, 
        hasUser: !!session?.user,
        userId: session?.user?.id 
      });
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        console.log("useAuth: Initial profile fetch for:", session.user.id);
        fetchUserProfile(session.user.id);
      } else {
        console.log("useAuth: No initial session, stopping loading");
        setLoading(false);
      }
    }).catch((error) => {
      console.error("useAuth: Exception getting session:", error);
      setLoading(false);
    });

    return () => {
      console.log("useAuth: Cleaning up auth hook");
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  return {
    user,
    session,
    loading,
    userRole,
    isAuthenticated: !!session
  };
};